import { html } from 'lit';
import { JVElement } from '../../lib/base';
/**
 * Schools (tenants) list page. Matches the wireframe 03-tenants.html.
 * Fetches from /api/operator/tenants.
 */
export class JvOperatorTenants extends JVElement {
    static { this.properties = {
        api: { type: Object },
        _tenants: { state: true },
        _filtered: { state: true },
        _search: { state: true },
        _loading: { state: true },
        _error: { state: true },
        _page: { state: true },
    }; }
    constructor() {
        super();
        this._pageSize = 10;
        this._tenants = [];
        this._filtered = [];
        this._search = '';
        this._loading = true;
        this._error = null;
        this._page = 1;
    }
    connectedCallback() {
        super.connectedCallback();
        this._fetch();
    }
    async _fetch() {
        this._loading = true;
        this._error = null;
        try {
            const tenants = await this.api.get('/api/operator/tenants');
            this._tenants = tenants;
            this._applyFilter();
        }
        catch (e) {
            this._error = e instanceof Error ? e.message : 'Failed to load tenants';
        }
        finally {
            this._loading = false;
        }
    }
    _onSearch(e) {
        this._search = e.target.value.toLowerCase();
        this._page = 1;
        this._applyFilter();
    }
    _applyFilter() {
        if (!this._search) {
            this._filtered = [...this._tenants];
        }
        else {
            this._filtered = this._tenants.filter(t => t.name.toLowerCase().includes(this._search) ||
                `${t.name.toLowerCase()}.justvue.io`.includes(this._search));
        }
    }
    get _paginated() {
        const start = (this._page - 1) * this._pageSize;
        return this._filtered.slice(start, start + this._pageSize);
    }
    get _totalPages() {
        return Math.max(1, Math.ceil(this._filtered.length / this._pageSize));
    }
    _prevPage() {
        if (this._page > 1)
            this._page--;
    }
    _nextPage() {
        if (this._page < this._totalPages)
            this._page++;
    }
    render() {
        if (this._loading) {
            return html `<div class="max-w-content mx-auto px-5 sm:px-8 py-10">
        <p class="text-sm text-jv-ink-2">Loading schools…</p>
      </div>`;
        }
        if (this._error) {
            return html `<div class="max-w-content mx-auto px-5 sm:px-8 py-10">
        <div class="jv-card"><div class="jv-card-body text-jv-danger text-sm">${this._error}</div></div>
      </div>`;
        }
        const total = this._tenants.length;
        const active = this._tenants.length;
        const totalParents = this._tenants.reduce((s, t) => s + t.userCount, 0);
        return html `<div class="max-w-content mx-auto px-5 sm:px-8 py-7">
      <!-- Page header -->
      <div class="flex flex-wrap items-end gap-4 mb-6">
        <jv-page-head title="Schools" subtitle="${total} tenant${total !== 1 ? 's' : ''}"></jv-page-head>
        <div class="ml-auto flex items-center gap-2">
          <div class="relative">
            <span class="jvi-office text-[16px] absolute left-3 top-1/2 -translate-y-1/2 text-jv-ink-2" aria-hidden="true"></span>
            <input
              type="search"
              class="jv-input pl-9 w-64"
              placeholder="Search schools…"
              aria-label="Search schools"
              .value=${this._search}
              @input=${this._onSearch}
            />
          </div>
          <jv-button variant="primary" icon="plus" label="Onboard school" href="#05-onboard"></jv-button>
        </div>
      </div>

      <!-- Stat cards -->
      <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <jv-stat-card label="Active tenants" .value=${active.toLocaleString()} trend="Returned by the operator API" icon="building" tone="success"></jv-stat-card>
        <jv-stat-card label="Parents served" .value=${totalParents.toLocaleString()} trend="Across all schools" icon="users" tone="default"></jv-stat-card>
        <jv-stat-card label="Cameras" .value=${this._tenants.reduce((s, t) => s + t.cameraCount, 0).toLocaleString()} trend="Assigned across tenants" icon="camera" tone="accent"></jv-stat-card>
        <jv-stat-card label="Agents" .value=${this._tenants.reduce((s, t) => s + t.agentCount, 0).toLocaleString()} trend="Edge connectivity inventory" icon="activity" tone="accent"></jv-stat-card>
      </div>

      <!-- Tenants table -->
      <div class="jv-card">
        <div class="jv-card-head flex items-center gap-3">
          <h3>All schools</h3>
          <span class="text-xs font-medium text-jv-ink-2">Showing ${this._paginated.length} of ${this._filtered.length}</span>
          <span class="ml-auto inline-flex items-center gap-2 text-xs text-jv-ink-2">
            <span class="jvi-info text-[15px]" aria-hidden="true"></span>
            Usage reflects the tenant's monthly viewing quota — enforced server-side at the relay.
          </span>
        </div>
        <div class="jv-card-body p-0">
          ${this._filtered.length === 0
            ? html `<div class="px-5 py-10 text-center text-sm text-jv-ink-2">No schools match "${this._search}".</div>`
            : html `<div class="overflow-x-auto">
              <table class="jv-table">
                <thead>
                  <tr>
                    <th>School</th>
                    <th class="text-right">Sites</th>
                    <th class="text-right">Parents</th>
                    <th class="text-right">Cameras</th>
                    <th>Time zone</th>
                    <th class="text-right">Manage</th>
                  </tr>
                </thead>
                <tbody>
                  ${this._paginated.map((t) => {
                const globalIdx = this._tenants.indexOf(t);
                const slug = t.name.toLowerCase().replace(/[^a-z0-9]+/g, '');
                const colorClass = globalIdx === 2 ? 'bg-jv-accent-050 text-jv-accent-600' : globalIdx === 3 ? 'bg-jv-warning-050 text-jv-warning' : 'bg-jv-primary-050 text-jv-primary';
                return html `<tr>
                        <td>
                          <a href="#04-tenant-detail/${t.id}" class="flex items-center gap-3 group">
                            <span class="grid place-items-center w-9 h-9 rounded-xl ${colorClass} shrink-0">
                              <span class="jvi-office text-[17px]" aria-hidden="true"></span>
                            </span>
                            <span class="min-w-0">
                              <b class="block text-jv-ink group-hover:text-jv-primary truncate">${t.name}</b>
                              <span class="text-xs text-jv-ink-2">${slug}.justvue.io</span>
                            </span>
                          </a>
                        </td>
                        <td class="text-right tabular-nums">${t.agentCount}</td>
                        <td class="text-right tabular-nums">${t.userCount}</td>
                        <td class="text-right tabular-nums">${t.cameraCount}</td>
                        <td class="text-sm text-jv-ink-2">${t.timeZoneId}</td>
                        <td class="text-right">
                          <jv-button variant="ghost" size="sm" icon="arrow" label="Open" href="#04-tenant-detail/${t.id}"></jv-button>
                        </td>
                      </tr>`;
            })}
                </tbody>
              </table>
            </div>`}
        </div>
        ${this._filtered.length > this._pageSize
            ? html `<div class="flex items-center justify-between px-5 py-3.5 border-t border-jv-border">
              <span class="text-xs text-jv-ink-2">Page ${this._page} of ${this._totalPages}</span>
              <div class="flex items-center gap-2">
                <jv-button variant="ghost" size="sm" label="Previous" @click=${this._prevPage}></jv-button>
                <jv-button variant="ghost" size="sm" label="Next" @click=${this._nextPage}></jv-button>
              </div>
            </div>`
            : null}
      </div>
    </div>`;
    }
}
